import React, { useState, useCallback, useMemo } from 'react';
import ReactFlow, {
    Node,
    Edge,
    addEdge,
    Connection,
    useNodesState,
    useEdgesState,
    Controls,
    Background,
    BackgroundVariant,
    MiniMap,
    NodeTypes,
    EdgeTypes,
    ReactFlowProvider,
} from 'reactflow';
import { Card, Button, Space, message, Modal, Form, Select, Input, Descriptions } from 'antd';
import { PlusOutlined, SaveOutlined, CopyOutlined } from '@ant-design/icons';
import { getQuestionsByAssessmentUuid } from 'api/operation/assessment';
import type { TQuestionItem } from 'types/operation';
import { getUrlParam } from 'utils/common';
import { useFlowData } from './hooks/useFlowData';
import { useNodeConfig } from './hooks/useNodeConfig';
import { useJsonExport } from './hooks/useJsonExport';
import NodeTypesComponent from './components/NodeTypes';
import EdgeTypesComponent from './components/EdgeTypes';
import Sidebar from './components/Sidebar';
import s from './s.module.less';
import 'reactflow/dist/style.css';

const { Option } = Select;

interface IVisualRuleBuilderProps {
    className?: string;
    assessmentUuid?: string;
    fullscreen?: boolean;
}

const VisualRuleBuilder: React.FC<IVisualRuleBuilderProps> = ({ className, assessmentUuid: propAssessmentUuid, fullscreen = false }) => {
    const [questions, setQuestions] = useState<TQuestionItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingNode, setEditingNode] = useState<Node | null>(null);
    const [form] = Form.useForm();

    const assessmentUuid = propAssessmentUuid || getUrlParam('uuid');

    // 使用自定义hooks管理流程数据
    const {
        nodes,
        edges,
        onNodesChange,
        onEdgesChange,
        addNode,
        updateNode,
        deleteNode,
        addEdge: addFlowEdge,
        updateEdge,
        deleteEdge,
        clearFlow,
    } = useFlowData();

    const {
        nodeConfigs,
        updateNodeConfig,
        getNodeConfig,
        validateNodeConfig,
    } = useNodeConfig();

    const {
        generateJson,
        exportJson,
        importJson,
        validateFlow,
    } = useJsonExport(nodes, edges, nodeConfigs, questions, assessmentUuid);

    // 加载问题数据
    const loadQuestions = useCallback(async () => {
        if (!assessmentUuid) {
            message.error('Assessment UUID not found in URL');
            return;
        }

        setLoading(true);
        try {
            const { data } = await getQuestionsByAssessmentUuid({
                assessmentUuid,
            });

            if (data?.data) {
                const filteredQuestions = data.data.filter(
                    (question: TQuestionItem) => !!question.googleFormId,
                );
                setQuestions(filteredQuestions);
            }
        } catch (error) {
            message.error('Failed to load questions');
        } finally {
            setLoading(false);
        }
    }, [assessmentUuid]);

    // 初始化时加载问题
    React.useEffect(() => {
        if (assessmentUuid) {
            loadQuestions();
        }
    }, [assessmentUuid, loadQuestions]);

    // 处理连线创建
    const onConnect = useCallback(
        (params: Connection) => {
            const newEdge = {
                ...params,
                id: `edge_${Date.now()}`,
                type: 'condition',
                data: {
                    condition: '',
                    label: '',
                },
            };
            addFlowEdge(newEdge);
        },
        [addFlowEdge]
    );

    // 处理节点双击编辑
    const onNodeDoubleClick = useCallback(
        (event: React.MouseEvent, node: Node) => {
            setEditingNode(node);
            const config = getNodeConfig(node.id);
            form.setFieldsValue(config);
            setModalVisible(true);
        },
        [getNodeConfig, form]
    );

    // 处理节点右键菜单
    const onNodeContextMenu = useCallback(
        (event: React.MouseEvent, node: Node) => {
            event.preventDefault();
            // 可以在这里添加右键菜单逻辑
        },
        []
    );

    // 处理边双击编辑
    const onEdgeDoubleClick = useCallback(
        (event: React.MouseEvent, edge: Edge) => {
            // 可以在这里添加边编辑逻辑
        },
        []
    );

    // 保存节点配置
    const handleSaveNode = useCallback(() => {
        form.validateFields().then((values) => {
            if (editingNode) {
                updateNodeConfig(editingNode.id, values);
                updateNode(editingNode.id, {
                    data: {
                        ...editingNode.data,
                        ...values,
                    },
                });
                setModalVisible(false);
                setEditingNode(null);
                form.resetFields();
                message.success('Node configuration saved');
            }
        }).catch((errorInfo) => {
            console.log('Validation failed:', errorInfo);
        });
    }, [editingNode, updateNodeConfig, updateNode, form]);

    // 取消编辑
    const handleCancelEdit = useCallback(() => {
        setModalVisible(false);
        setEditingNode(null);
        form.resetFields();
    }, [form]);

    // 生成JSON
    const handleGenerateJson = useCallback(() => {
        try {
            const jsonConfig = generateJson();
            console.log('Generated JSON:', jsonConfig);
            message.success('JSON generated successfully');
        } catch (error) {
            message.error('Failed to generate JSON');
            console.error('JSON generation error:', error);
        }
    }, [generateJson]);

    // 复制JSON
    const handleCopyJson = useCallback(() => {
        try {
            const jsonConfig = generateJson();
            navigator.clipboard.writeText(JSON.stringify(jsonConfig, null, 2));
            message.success('JSON copied to clipboard');
        } catch (error) {
            message.error('Failed to copy JSON');
        }
    }, [generateJson]);

    // 清空流程
    const handleClearFlow = useCallback(() => {
        Modal.confirm({
            title: 'Clear Flow',
            content: 'Are you sure you want to clear all nodes and edges?',
            onOk: () => {
                clearFlow();
                message.success('Flow cleared');
            },
        });
    }, [clearFlow]);

    // 节点类型定义
    const nodeTypes: NodeTypes = useMemo(
        () => ({
            start: NodeTypesComponent.StartNode,
            question: NodeTypesComponent.QuestionNode,
            result: NodeTypesComponent.ResultNode,
            jump: NodeTypesComponent.JumpNode,
        }),
        []
    );

    // 边类型定义
    const edgeTypes: EdgeTypes = useMemo(
        () => ({
            condition: EdgeTypesComponent.ConditionEdge,
        }),
        []
    );

    if (loading) {
        return <div>Loading questions...</div>;
    }

    return (
        <div className={`${s.visualRuleBuilder} ${fullscreen ? s.fullscreen : ''} ${className || ''}`}>
            <div className={s.header}>
                <Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setModalVisible(true)}
                    >
                        Add Node
                    </Button>
                    <Button
                        icon={<SaveOutlined />}
                        onClick={handleGenerateJson}
                    >
                        Generate JSON
                    </Button>
                    <Button
                        icon={<CopyOutlined />}
                        onClick={handleCopyJson}
                    >
                        Copy JSON
                    </Button>
                    <Button
                        danger
                        onClick={handleClearFlow}
                    >
                        Clear Flow
                    </Button>
                </Space>
            </div>

            <div className={s.flowContainer}>
                <Sidebar
                    questions={questions}
                    onAddNode={addNode}
                    onImportJson={importJson}
                />
                
                <div className={s.flowArea}>
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onNodeDoubleClick={onNodeDoubleClick}
                        onNodeContextMenu={onNodeContextMenu}
                        onEdgeDoubleClick={onEdgeDoubleClick}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        fitView
                        attributionPosition="bottom-left"
                    >
                        <Background variant={BackgroundVariant.Dots} />
                        <Controls />
                        <MiniMap />
                    </ReactFlow>
                </div>
            </div>

            {/* 节点编辑弹窗 */}
            <Modal
                title={editingNode ? 'Edit Node' : 'Add Node'}
                open={modalVisible}
                onOk={handleSaveNode}
                onCancel={handleCancelEdit}
                width={600}
                okText="Save"
                cancelText="Cancel"
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        type: 'question',
                        triggerTiming: 'after_last_question',
                    }}
                >
                    <Form.Item
                        label="Node Type"
                        name="type"
                        rules={[{ required: true, message: 'Please select node type' }]}
                    >
                        <Select placeholder="Select node type">
                            <Option value="start">Start</Option>
                            <Option value="question">Question</Option>
                            <Option value="result">Result</Option>
                            <Option value="jump">Jump</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.type !== currentValues.type
                        }
                    >
                        {({ getFieldValue }) => {
                            const nodeType = getFieldValue('type');
                            
                            if (nodeType === 'question') {
                                return (
                                    <>
                                        <Form.Item
                                            label="Question"
                                            name="questionId"
                                            rules={[{ required: true, message: 'Please select a question' }]}
                                        >
                                            <Select placeholder="Select question">
                                                {questions.map((q) => (
                                                    <Option key={q.id} value={q.id}>
                                                        {q.title}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                        
                                        <Form.Item
                                            label="Answer Condition"
                                            name="answerCondition"
                                        >
                                            <Input placeholder="Enter answer condition" />
                                        </Form.Item>
                                    </>
                                );
                            }
                            
                            if (nodeType === 'result') {
                                return (
                                    <>
                                        <Form.Item
                                            label="Result Type"
                                            name="resultType"
                                            rules={[{ required: true, message: 'Please select result type' }]}
                                        >
                                            <Select placeholder="Select result type">
                                                <Option value="not_suitable">Not Suitable</Option>
                                                <Option value="text_visit">Text Visit</Option>
                                                <Option value="continue">Continue</Option>
                                                <Option value="add_note">Add Note</Option>
                                            </Select>
                                        </Form.Item>
                                        
                                        <Form.Item
                                            noStyle
                                            shouldUpdate={(prevValues, currentValues) =>
                                                prevValues.resultType !== currentValues.resultType
                                            }
                                        >
                                            {({ getFieldValue }) => {
                                                const resultType = getFieldValue('resultType');
                                                
                                                if (resultType === 'continue') {
                                                    return (
                                                        <Form.Item
                                                            label="Skip to Question"
                                                            name="skipToQuestionId"
                                                        >
                                                            <Select placeholder="Select question to skip to">
                                                                {questions.map((q) => (
                                                                    <Option key={q.id} value={q.id}>
                                                                        {q.title}
                                                                    </Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>
                                                    );
                                                }
                                                
                                                if (resultType === 'add_note') {
                                                    return (
                                                        <>
                                                            <Form.Item
                                                                label="Selected Options"
                                                                name="selectedOptions"
                                                            >
                                                                <Select
                                                                    mode="multiple"
                                                                    placeholder="Select options"
                                                                >
                                                                    <Option value="only_video_visit">Only Video Visit</Option>
                                                                    <Option value="no_systemic_hrt">No systemic HRT</Option>
                                                                    <Option value="others">Others</Option>
                                                                </Select>
                                                            </Form.Item>
                                                            
                                                            <Form.Item
                                                                label="Custom Text"
                                                                name="customText"
                                                            >
                                                                <Input.TextArea
                                                                    placeholder="Enter custom text"
                                                                    rows={3}
                                                                />
                                                            </Form.Item>
                                                        </>
                                                    );
                                                }
                                                
                                                return null;
                                            }}
                                        </Form.Item>
                                    </>
                                );
                            }
                            
                            if (nodeType === 'jump') {
                                return (
                                    <Form.Item
                                        label="Target Question"
                                        name="targetQuestionId"
                                        rules={[{ required: true, message: 'Please select target question' }]}
                                    >
                                        <Select placeholder="Select target question">
                                            {questions.map((q) => (
                                                <Option key={q.id} value={q.id}>
                                                    {q.title}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                );
                            }
                            
                            return null;
                        }}
                    </Form.Item>

                    <Form.Item
                        label="Trigger Timing"
                        name="triggerTiming"
                        rules={[{ required: true, message: 'Please select trigger timing' }]}
                    >
                        <Select placeholder="Select trigger timing">
                            <Option value="immediate">Immediate</Option>
                            <Option value="after_last_question">After Last Question</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

// 使用ReactFlowProvider包装组件
const VisualRuleBuilderWithProvider: React.FC<IVisualRuleBuilderProps> = (props) => (
    <ReactFlowProvider>
        <VisualRuleBuilder {...props} />
    </ReactFlowProvider>
);

export default VisualRuleBuilderWithProvider;
